import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { TokenService } from '../service/token.service';
import { ChannelMappingInfo } from '../model/ChannelMapping.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DatePipe, Location } from '@angular/common';

@Component({
  selector: 'app-channel-mapping',
  templateUrl: './channel-mapping.component.html',
  styleUrls: ['./channel-mapping.component.css']
})
export class ChannelMappingComponent implements OnInit {
  ChannelMappingList: ChannelMappingInfo[] = [];
  ChannelTypeCode: string = '';
  ChannelTypeName: string = '';
  ChannelTypeFlagDealer: string = '';
  loading: boolean = false;
  environment: number = 10;
  currentPageNumber: number = 1;
  totalPages: number = 1;
  totalItems: number = 0;
  baseurl: string = '';
  canEdit: boolean = true;
  updatedMappings: ChannelMappingInfo[] = [];
  form: FormGroup;
  isOnUpdate: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private httpClient: HttpClient,
    private TokenService: TokenService,
    private formBuilder: FormBuilder,
    private location: Location
  ) {
    this.baseurl = environment.base_api;
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      ChannelMappingId: [0],
      ChannelMappingCode: [null],
      ChannelMappingName: [null, Validators.required],
      ChannelMappingPhone: [null],
      ChannelMappingEmail: [null],
      ChannelMappingFlagActive: ['Y'],
      ChannelTypeCode: [null],
      ChannelTypeName: [null],
      ChannelTypeFlagDealer: [null],
      CreateBy: [null],
      UpdateBy: [null]
    });

    // Get state from navigation
    const state = history.state;

    // Check if state is undefined or doesn't contain required properties
    if (!state || !state.ChannelTypeId || !state.ChannelTypeCode) {
      console.log('Invalid channel type parameters received, redirecting back...');
      this.router.navigate(['/channel-type-management']);
      return;
    }

    this.ChannelTypeCode = state.ChannelTypeCode;
    this.ChannelTypeName = state.ChannelTypeName;
    this.ChannelTypeFlagDealer = state.ChannelTypeFlagDealer;

    // Load channel mapping data
    this.getChannelMappingList();
  }

  async getChannelMappingList() {
    this.loading = true;
    try {
      const auth_token = await this.TokenService.RefreshToken();
      if (!auth_token) {
        console.error('No valid token');
        alert('Session expired. Please login again.');
        return;
      }

      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth_token}`
        })
      };

      const objectjson = {
        ChannelTypeCode: this.ChannelTypeCode,
        ChannelMappingType: "C",
        rowFetch: this.environment,
        rowOFFSet: ((this.currentPageNumber - 1) < 0 ? 0 : (this.currentPageNumber - 1)) * this.environment
      };

      const response = await this.httpClient.post<any>(
        this.baseurl + "Channel/GetChannelMappingList",
        JSON.stringify(objectjson),
        httpOptions
      ).toPromise();

      this.ChannelMappingList = response;
      this.totalItems = this.ChannelMappingList.length;

      if (this.ChannelMappingList.length <= 0) {
        this.currentPageNumber = 0;
      } else {
        if (this.currentPageNumber == 0) this.currentPageNumber = 1;
        this.totalItems = Number(this.ChannelMappingList[0].itemTotal);
      }

      this.totalPages = Math.ceil(this.totalItems / this.environment);
    } catch (error) {
      console.error('Error loading channel mapping:', error);
      alert('An error occurred while loading channel mapping data. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  LoadChannelMappingList(event: any) {
    //this.currentPageNumber = event.currentPageNumber;
    //this.environment = event.environment;
    if (event != null) {
      this.currentPageNumber = event.currentPageNumber;
      this.totalPages = event.totalPages;
    }
    this.getChannelMappingList();
  }

  onCheckboxChange(item: ChannelMappingInfo, event: any) {
    // Store the updated mapping
    const updatedItem = {
      ...item,
      ChannelMappingFlagActive: event.target.checked ? 'Y' : 'N'
    };

    // Check if already exists in updatedMappings
    const existingIndex = this.updatedMappings.findIndex(x => x.ChannelMappingId === item.ChannelMappingId);
    if (existingIndex >= 0) {
      this.updatedMappings[existingIndex] = updatedItem;
    } else {
      this.updatedMappings.push(updatedItem);
    }
  }

  async btnClick_submitMapping() {
    if (this.updatedMappings.length === 0) {
      alert('No changes to submit');
      return;
    }

    try {
      const auth_token = await this.TokenService.RefreshToken();
      if (!auth_token) {
        console.error('No valid token');
        alert('Session expired. Please login again.');
        return;
      }

      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth_token}`
        })
      };

      // Update each mapping
      for (const mapping of this.updatedMappings) {
        const response = await this.httpClient.put<any>(
          this.baseurl + `Channel/UpdateChannelMapping`,
          JSON.stringify(mapping),
          httpOptions
        ).toPromise();

        if (!response || !response.success) {
          throw new Error('Failed to update mapping');
        }
      }

      alert('Channel mappings updated successfully');
      // Clear updated mappings and refresh list
      this.updatedMappings = [];
      await this.getChannelMappingList();
    } catch (error) {
      console.error('Error submitting mappings:', error);
      alert('An error occurred while submitting mappings. Please try again.');
    }
  }

  async btnSave() {
    if (this.form.invalid) {
      this.validateAllFormFields(this.form);
      return;
    }

    const auth_token = await this.TokenService.RefreshToken();
    if (!auth_token) {
      console.error('No valid token');
      alert('Session expired. Please login again.');
      return;
    }

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth_token}`
      })
    };

    try {
      const formData = this.form.value;
      
      // Set CreateBy and UpdateBy based on operation type
      if (this.isOnUpdate) {
        formData.UpdateBy = localStorage.getItem("EmpCode");
      } else {
        formData.CreateBy = localStorage.getItem("EmpCode");
        formData.ChannelTypeCode = this.ChannelTypeCode;
        formData.ChannelMappingType = "C";
      }

      const response = await this.httpClient.post<any>(
        this.baseurl + "Channel/SaveChannelMapping",
        JSON.stringify(formData),
        httpOptions
      ).toPromise();

      if (response === true) {
        this.Clearinput();
        await this.getChannelMappingList();
        $('#ModalChannelMapping').modal('hide');
        
        // Show success message
        const message = this.isOnUpdate ? 'Channel mapping updated successfully' : 'Channel mapping created successfully';
        alert(message);
      } else {
        // Handle unsuccessful response
        console.error('Save failed');
        alert('Failed to save channel mapping');
      }
    } catch (error) {
      console.error('Error saving channel mapping:', error);
      alert('An error occurred while saving channel mapping. Please try again.');
    }
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      } else {
        control.markAsTouched({ onlySelf: true });
      }
    });
  }

  Clearinput() {
    this.form.patchValue({
      ChannelMappingId: 0,
      ChannelMappingCode: null,
      ChannelMappingName: null,
      ChannelMappingPhone: null,
      ChannelMappingEmail: null,
      ChannelMappingFlagActive: 'Y',
      ChannelTypeCode: this.ChannelTypeCode,
      ChannelTypeName: this.ChannelTypeName,
      ChannelTypeFlagDealer: this.ChannelTypeFlagDealer
    });
  }

  async btnClick_onCreateChannelMapping() {
    this.isOnUpdate = false;
    this.Clearinput();
    $('#ModalChannelMapping').modal('show');
  }

  async btnClick_onUpdateChannelMapping(channelMappingId: number) {
    try {
      const channelMapping = this.ChannelMappingList.find(x => x.ChannelMappingId === channelMappingId);
      if (channelMapping) {
        this.isOnUpdate = true;
        this.form.patchValue({
          ChannelMappingId: channelMapping.ChannelMappingId,
          ChannelMappingCode: channelMapping.ChannelMappingCode,
          ChannelMappingName: channelMapping.ChannelMappingName,
          ChannelMappingPhone: channelMapping.ChannelMappingPhone,
          ChannelMappingEmail: channelMapping.ChannelMappingEmail,
          ChannelMappingFlagActive: channelMapping.ChannelMappingFlagActive,
          ChannelTypeCode: this.ChannelTypeCode,
          ChannelTypeName: this.ChannelTypeName,
          ChannelTypeFlagDealer: this.ChannelTypeFlagDealer
        });
        $('#ModalChannelMapping').modal('show');
      }
    } catch (error) {
      console.error('Error updating channel mapping:', error);
      alert('An error occurred while updating channel mapping. Please try again.');
    }
  }

  async btnClick_deleteChannelMapping(channelMappingId: number) {
    if (!confirm('Are you sure you want to delete this channel mapping?')) {
      return;
    }

    try {
      const auth_token = await this.TokenService.RefreshToken();
      if (!auth_token) {
        console.error('No valid token');
        alert('Session expired. Please login again.');
        return;
      }

      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth_token}`
        })
      };

      const response = await this.httpClient.delete<any>(
        this.baseurl + `Channel/DeleteChannelMapping/${channelMappingId}`,
        httpOptions
      ).toPromise();

      if (response && response.success) {
        alert('Channel mapping deleted successfully');
        await this.getChannelMappingList();
      } else {
        alert('Failed to delete channel mapping');
      }
    } catch (error) {
      console.error('Error deleting channel mapping:', error);
      alert('An error occurred while deleting channel mapping. Please try again.');
    }
  }

  GoBack() {
    this.location.back()
  }
}
